<template>
  <div>
    <div class="row mt-4">
      <div class="col-6">
        <h4>
          {{ $t('PAGES.TITLE.PUBLISH_PAGE') }}
        </h4>
        <p class="mt-3">
          {{ $t('PAGES.TEXT.PUBLISH_YOUR_PAGE_WHEN') }}
        </p>
      </div>
      <div class="col-6 d-flex justify-content-end">
        <HMSwitch :value="isPublished" @input="updateIsPublished" />
      </div>
    </div>
    <hr class="my-4" />
    <ValidationObserver ref="form" v-slot="{ dirty }" v-if="form">
      <HMGlobalForm :isDirty="dirty" :saveFun="save" />
      <div class="row justify-content-between">
        <div class="col-5">
          <h4>
            {{ $t('PAGES.TITLE.PAGE_TITLE') }}
          </h4>
          <p class="mt-3">
            {{ $t('PAGES.TEXT.CHOOSE_A_TITLE_FOR') }}
          </p>
        </div>
        <div class="col-5">
          <HMTextInput
            class="w-100"
            :label="$t('PAGES.LABEL.PAGE_TITLE')"
            v-model="form.name"
          ></HMTextInput>
        </div>
      </div>
      <hr class="my-4" />
      <div class="row justify-content-between">
        <div class="col-5">
          <h4>
            {{ $t('PAGES.TITLE.PAGE_URL') }}
          </h4>
          <p class="mt-3">
            {{ $t('PAGES.TEXT.SET_UP_A_CUSTOM_URL_FOR') }}
          </p>
          <p class="mt-2 mb-1">{{ $t('PAGES.TITLE.PAGE_URL') }}:</p>
          <a :href="`${defaultPagesLocationUrl}/${pageSlug}`" target="_blank">
            {{ defaultPagesLocationUrl }}/{{ pageSlug }}
          </a>
          <a
            :href="`${pagesLocationUrl}/${pageSlug}`"
            target="_blank"
            v-if="isPagesDomainValid"
          >
            {{ pagesLocationUrl }}/{{ pageSlug }}
          </a>
        </div>
        <div class="col-5">
          <HMTextInput
            class="w-100"
            :label="`URL: ${teacherStudioUrl}/..`"
            v-model="pageSlug"
          ></HMTextInput>
        </div>
      </div>
      <hr class="my-4" />
      <div class="row justify-content-between">
        <div class="col-5">
          <h4>
            {{ $t('PAGES.TITLE.CUSTOM_FAVICON') }}
          </h4>
          <p class="mt-3">
            {{ $t('PAGES.TEXT.UPLOAD_A_CUSTOM_ICON_TO_SHOW') }}
          </p>
        </div>
        <div class="col-5">
          <HMS3ImageUploader
            v-model="form.favicon"
            :title="$t('PAGES.TITLE.UPLOAD_A_CUSTOM_FAVICON')"
            :canvaWidth="180"
            :canvaHeight="180"
            :isUnsplashEnabled="false"
            :cropper="false"
            id="favicon"
            inputName="favicon-upload"
          />
        </div>
      </div>
      <hr class="my-4" />
      <div class="row justify-content-between">
        <div class="col-12">
          <h4>
            {{ $t('PAGES.TITLE.CUSTOM_PAGE_HEADER_CODE') }}
          </h4>
          <HMTextarea
            class="w-100"
            :label="$t('PAGES.TEXT.YOU_CAN_USE_THIS_TO_ADD_CODE_SNIPPETS')"
            name="insert_head"
            :rows="10"
            :maxLength="65535"
            :showTextLength="false"
            :useDynamicHeight="false"
            v-model="form.insert_head"
          ></HMTextarea>
        </div>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { ValidationObserver } from 'vee-validate';
import { pick } from 'lodash';
import { errorToMessage } from '@/core/utils/errors';
import { slugify } from '@/core/utils/utils';

export default Vue.extend({
  name: 'page-settings',
  components: {
    ValidationObserver,
  },
  data() {
    return {
      form: undefined,
      isPublished: undefined,
    };
  },
  computed: {
    ...mapGetters({
      page: 'page/page',
      teacherStudioUrl: 'user/teacherStudioUrl',
      isPagesDomainValid: 'setting/isPagesDomainValid',
      pagesLocationUrl: 'setting/pagesLocationUrl',
    }),
    defaultPagesLocationUrl() {
      return `${process.env.VUE_APP_PAGE_BUILDER_BASE_URL}/${this.teacherStudioUrl}`;
    },
    pageSlug: {
      get() {
        return this.form.slug || '';
      },
      set(url) {
        this.$set(this.form, 'slug', this.slugify(url));
      },
    },
  },
  methods: {
    slugify,
    async save() {
      try {
        await this.$store.dispatch('page/updatePage', {
          id: this.page.id,
          data: this.formToData(this.form, this.page),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
          text: errorToMessage(
            error.response,
            `${this.$t(
              'PAGES.NOTIFICATIONS.FAILED_TO_UPDATED_A_PAGE'
            )}: ${error}`
          ),
          duration: -1,
        });
      } finally {
        this.$refs.form?.reset();
      }
    },
    formToData(form, page) {
      const data = pick(form, ['name', 'slug', 'insert_head', 'favicon']);

      data.status = form.isPublished ? 'published' : 'unpublished';

      return { ...page, ...data };
    },
    pageToForm(page) {
      const form = pick(page, ['name', 'slug', 'insert_head', 'favicon']);

      return form;
    },
    async updateIsPublished(isPublished) {
      try {
        if (isPublished) {
          await this.$store.dispatch('page/publishPage', this.page.id);
        } else {
          await this.$store.dispatch('page/unpublishPage', this.page.id);
        }
        this.isPublished = isPublished;
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
          text: errorToMessage(
            error.response,
            `${this.$t(
              'PAGES.NOTIFICATIONS.FAILED_TO_UPDATED_A_PAGE'
            )}: ${error}`
          ),
          duration: -1,
        });
      }
    },
  },
  created() {
    this.isPublished = this.page.status === 'published';
    this.form = this.pageToForm(this.page);
  },
});
</script>
